import React, { useState, useCallback } from "react";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import "./AdminChangePassword.css";
import CustomInput from "components/widgets/custom-input/CustomInput.js";
import CustomButton from "components/widgets/custom-button/CustomButton.js";
import MainLayout from "components/layout/main-layout/MainLayout.js";
import { titles } from "constants/titles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  checkForSameInputs,
  isEmpty,
  isSessionTimeExpired,
  isValidPassword,
} from "utility";
import { changePassword } from "redux/auth/actions";
import { FIELD_CONFIG, VALIDATION_FIELDS } from "./config";
import alerts from "constants/alerts";

const AdminChangePassword = () => {
  const [loginState, setLoginState] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Mobile-like validation states
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [errorState, setErrorState] = useState({
    show: false,
    fieldErrors: { ...VALIDATION_FIELDS },
    fieldMessages: { ...VALIDATION_FIELDS }
  });

  const isLoading = useSelector((store) => store?.auth?.loading);
  const history = useHistory();
  const dispatch = useDispatch();

  // Initialize validation hook for snackbar
  const { snackbarState, dismissSnackbar, handleApiResponse } = useAdvancedValidation();

  const clearError = (fieldName) => {
    if (fieldName) {
      setErrorState(prev => ({
        ...prev,
        fieldErrors: {
          ...prev.fieldErrors,
          [fieldName]: false
        },
        fieldMessages: {
          ...prev.fieldMessages,
          [fieldName]: ""
        }
      }));
    } else {
      setErrorState({
        show: false,
        fieldErrors: { ...VALIDATION_FIELDS },
        fieldMessages: { ...VALIDATION_FIELDS }
      });
    }
  };

  // Consolidated validation function using field configuration
  const validateField = (fieldName, value, allValues = loginState) => {
    const config = FIELD_CONFIG?.[fieldName];
    if (!config) return { fieldError: false, fieldMessage: "" };

    const { validation } = config;
    let fieldError = false;
    let fieldMessage = "";

    // Required field validation
    if (validation?.required && isEmpty(value)) {
      fieldError = true;
      fieldMessage = alerts?.incorrect_details;
      return { fieldError, fieldMessage };
    }

    // Minimum length validation
    if (!isEmpty(value) && validation?.minLength && !isValidPassword(value)) {
      fieldError = true;
      fieldMessage = validation?.customMessage || alerts?.incorrect_details;
      return { fieldError, fieldMessage };
    }

    // Field matching validation (for confirm password)
    if (validation?.matchField && !isEmpty(value)) {
      const matchValue = allValues?.[validation.matchField];
      if (!checkForSameInputs(value, matchValue)) {
        fieldError = true;
        fieldMessage = validation?.matchMessage || "Fields do not match";
        return { fieldError, fieldMessage };
      }
    }

    return { fieldError, fieldMessage };
  };

  // Validate all fields at once
  const validateAllFields = () => {
    const fieldErrors = { ...VALIDATION_FIELDS };
    const fieldMessages = { ...VALIDATION_FIELDS };
    let hasErrors = false;

    Object.keys(FIELD_CONFIG || {}).forEach(fieldName => {
      const value = loginState?.[fieldName];
      const { fieldError, fieldMessage } = validateField(fieldName, value);

      if (fieldError) {
        fieldErrors[fieldName] = true;
        fieldMessages[fieldName] = fieldMessage || alerts?.incorrect_details;
        hasErrors = true;
      }
    });

    return { fieldErrors, fieldMessages, hasErrors };
  };

  const onResetPassword = async () => {
    let timeDifference = await isSessionTimeExpired();

    // Set validation triggered to true (enables real-time validation)
    setValidationTriggered(true);

    // Clear previous errors
    clearError();

    // Validate all fields using consolidated function
    const { fieldErrors, fieldMessages, hasErrors } = validateAllFields();

    // Set all errors at once if any validation failed
    if (hasErrors) {
      setErrorState({
        show: true,
        fieldErrors,
        fieldMessages
      });
      // handleApiResponse("Incorrect or Missing Details Below", "error");
      return;
    }

    if (timeDifference) {
      // Handle session expiry - you may need to implement this
      return;
    } else {
      changePassword(
        dispatch,
        {
          existing_password: loginState?.currentPassword,
          new_password: loginState?.newPassword,
        },
        history,
        handleApiResponse
      );
    }
  };

  const onValueChange = useCallback((type, e) => {
    let newText = e?.target?.value;
    setLoginState((prev) => ({ ...prev, [type]: newText }));

    // Only validate if validation has been triggered (after submit click)
    if (validationTriggered) {
      // Validate the current field
      const { fieldError, fieldMessage } = validateField(type, newText);

      // Update field error state
      setErrorState(prev => ({
        ...prev,
        show: true, // Keep show true when validation is triggered
        fieldErrors: { ...prev?.fieldErrors, [type]: fieldError },
        fieldMessages: { ...prev?.fieldMessages, [type]: fieldMessage || "" }
      }));

      // Check if all fields are valid
      const updatedFieldErrors = { ...errorState?.fieldErrors, [type]: fieldError };
      const hasAnyErrors = Object.values(updatedFieldErrors || {}).some(Boolean);

      // Show/hide snackbar based on validation errors
      if (fieldError && !snackbarState.show) {
        // handleApiResponse("Incorrect or Missing Details Below", "error");
      } else if (!fieldError && !hasAnyErrors && snackbarState.show) {
        dismissSnackbar();
      }
    } else {
      // Clear field error when user starts typing (before validation triggered)
      setErrorState(prev => {
        if (prev.fieldErrors?.[type]) {
          return {
            ...prev,
            fieldErrors: { ...prev.fieldErrors, [type]: false },
            fieldMessages: { ...prev.fieldMessages, [type]: "" },
          };
        }
        return prev;
      });

      // Clear snackbar when user starts typing
      if (snackbarState.show) {
        dismissSnackbar();
      }
    }
  }, [validationTriggered, errorState?.fieldErrors, snackbarState.show, handleApiResponse, dismissSnackbar]);

  // Check if there are any validation errors
  const hasValidationErrors = Object.values(errorState.fieldErrors).some(error => error);

  // Don't auto-close snackbar if there are validation errors
  const snackbarAutoHideDuration = hasValidationErrors && validationTriggered
    ? 0
    : snackbarState?.autoHideDuration;

  return (
    <>
      {snackbarState?.show && (
        <div className="acp_snackbar_animated">
          <MessageSnackBar
            message={snackbarState?.message}
            isVisible={true}
            onDismiss={dismissSnackbar}
            type={snackbarState?.type}
            autoHideDuration={snackbarAutoHideDuration}
            constrainWidth={false}
          />
        </div>
      )}
      <MainLayout
        text={titles?.change_password}
        isBackHide={false}
        isNavbar={false}
        isLoading={isLoading}
        white={true}
      >
        <div className="acp_container">
          <p className="acp_header_text">
            {titles?.new_password_must_be_different}
          </p>
          <div className="acp_container">
            <CustomInput
              label={titles?.current_password}
              type="password"
              placeholder={"*********"}
              value={loginState?.currentPassword}
              onChange={(e) => {
                onValueChange("currentPassword", e);
              }}
              error={validationTriggered && errorState.fieldErrors?.currentPassword && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.currentPassword && errorState.show ? errorState.fieldMessages?.currentPassword : ""}
            />
            <CustomInput
              label={titles?.new_password}
              type="password"
              required={true}
              placeholder={titles?.must_be_minimum_8}
              value={loginState?.newPassword}
              onChange={(e) => {
                onValueChange("newPassword", e);
              }}
              labelClassName={"cp_input_top"}
              error={validationTriggered && errorState.fieldErrors?.newPassword && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.newPassword && errorState.show ? errorState.fieldMessages?.newPassword : ""}
            />
            <CustomInput
              label={titles?.confirm_new_password}
              type="password"
              required={true}
              placeholder={titles?.passwords_must_match}
              value={loginState?.confirmPassword}
              onChange={(e) => {
                onValueChange("confirmPassword", e);
              }}
              labelClassName={'mt30'}
              error={validationTriggered && errorState.fieldErrors?.confirmPassword && errorState.show}
              errorMessage={validationTriggered && errorState.fieldErrors?.confirmPassword && errorState.show ? errorState.fieldMessages?.confirmPassword : ""}
            />
          </div>
          <CustomButton
            width={"100%"}
            text={titles?.save_changes}
            className={'mt30'}
            disabled={hasValidationErrors}
            showErrorMessage={validationTriggered && hasValidationErrors}
            onClick={() => onResetPassword()}
          />
        </div>
      </MainLayout>
    </>
  );
};

export default AdminChangePassword;
